import { ColumnDef } from '@tanstack/react-table';
import { Link, useNavigate } from 'react-router-dom';
import { Client } from '@/types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { DataTable } from '@/components/ui/data-table';
import {
  MoreHorizontal,
  Eye,
  Pencil,
  Trash2,
  Building2,
  User,
  Mail,
  Clock,
} from 'lucide-react';

interface ClientWithStats extends Client {
  totalHours?: number;
  earnings?: number;
  activeTasks?: number;
}

interface ClientsTableProps {
  clients: ClientWithStats[];
  isLoading?: boolean;
  onDelete?: (client: Client) => void;
  emptyState?: React.ReactNode;
}

export function ClientsTable({
  clients,
  isLoading,
  onDelete,
  emptyState,
}: ClientsTableProps) {
  const navigate = useNavigate();

  const columns: ColumnDef<ClientWithStats>[] = [
    {
      accessorKey: 'name',
      header: 'Name',
      cell: ({ row }) => {
        const client = row.original;
        return (
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              {client.type === 'business' ? (
                <Building2 className="h-5 w-5 text-primary" />
              ) : (
                <User className="h-5 w-5 text-primary" />
              )}
            </div>
            <div>
              <Link
                to={`/clients/${client.id}`}
                className="font-medium text-foreground hover:text-primary transition-colors"
              >
                {client.name}
              </Link>
              {client.businessName && (
                <p className="text-sm text-muted-foreground">
                  {client.businessName}
                </p>
              )}
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const type = row.getValue('type') as string;
        return (
          <Badge variant={type === 'business' ? 'default' : 'secondary'}>
            {type === 'business' ? 'Business' : 'Private'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'email',
      header: 'Contact',
      cell: ({ row }) => {
        const email = row.getValue('email') as string | undefined;
        if (!email) return <span className="text-muted-foreground">-</span>;
        return (
          <a
            href={`mailto:${email}`}
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors"
          >
            <Mail className="h-4 w-4" />
            <span className="truncate max-w-[200px]">{email}</span>
          </a>
        );
      },
    },
    {
      accessorKey: 'totalHours',
      header: 'Hours',
      cell: ({ row }) => {
        const hours = row.getValue('totalHours') as number | undefined;
        return (
          <div className="flex items-center gap-2 text-muted-foreground">
            <Clock className="h-4 w-4" />
            <span>{hours?.toFixed(1) || '0'}h</span>
          </div>
        );
      },
    },
    {
      accessorKey: 'earnings',
      header: 'Earnings',
      cell: ({ row }) => {
        const earnings = row.getValue('earnings') as number | undefined;
        const currency = row.original.currency || 'EUR';
        const symbol = currency === 'EUR' ? '€' : currency === 'USD' ? '$' : currency;
        return (
          <span className="font-medium">
            {symbol} {(earnings || 0).toLocaleString()}
          </span>
        );
      },
    },
    {
      accessorKey: 'activeTasks',
      header: 'Tasks',
      cell: ({ row }) => {
        const tasks = row.getValue('activeTasks') as number | undefined;
        return (
          <Badge variant="outline">
            {tasks || 0} active
          </Badge>
        );
      },
    },
    {
      id: 'actions',
      cell: ({ row }) => {
        const client = row.original;
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8">
                <MoreHorizontal className="h-4 w-4" />
                <span className="sr-only">Open menu</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}`)}>
                <Eye className="mr-2 h-4 w-4" />
                View
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => navigate(`/clients/${client.id}/edit`)}>
                <Pencil className="mr-2 h-4 w-4" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => onDelete?.(client)}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="mr-2 h-4 w-4" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        );
      },
    },
  ];

  return (
    <DataTable
      columns={columns}
      data={clients}
      isLoading={isLoading}
      emptyState={emptyState}
      pageSize={10}
    />
  );
}
